import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import "./Navbar.css";
import { SITE } from "../../data/site";

const SECTION_LINKS = [
  { label: "About", id: "about" },
  { label: "Expertise", id: "expertise" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(() => window.scrollY > 12);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const goToSection = useCallback(
    (id: string) => {
      setOpen(false);
      if (location.pathname === "/") {
        document
          .getElementById(id)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        navigate(`/#${id}`);
      }
    },
    [location.pathname, navigate]
  );

  return (
    <header className="nav">
      {/* backdrop-filter lives on this wrapper, not on <header>, because an
          element with backdrop-filter becomes a containing block for its
          position:fixed descendants — that would trap the mobile menu
          panel below inside this bar's tiny height instead of the
          viewport whenever the page is scrolled. */}
      <div className={`nav-bar ${scrolled ? "nav-bar--scrolled" : ""}`}>
        <nav className="nav-inner container" aria-label="Main navigation">
          <Link to="/" className="nav-wordmark" onClick={() => setOpen(false)}>
            Konstantinos&nbsp;Efkarpidis
          </Link>

          <div className="nav-links">
            {SECTION_LINKS.map((link) => (
              <button
                key={link.id}
                type="button"
                className="nav-link"
                onClick={() => goToSection(link.id)}
              >
                {link.label}
              </button>
            ))}
            <Link
              to="/apps"
              className={`nav-link nav-link--apps ${
                location.pathname.startsWith("/apps") ? "is-active" : ""
              }`}
            >
              Apps
            </Link>
            <span className="nav-divider" aria-hidden="true" />
            <a
              className="nav-link"
              href={SITE.github}
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub
            </a>
            <a
              className="nav-link"
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
            >
              LinkedIn
            </a>
          </div>

          <button
            type="button"
            className={`nav-burger ${open ? "is-open" : ""}`}
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
          >
            <span />
            <span />
          </button>
        </nav>
      </div>

      <div
        id="mobile-menu"
        className={`nav-mobile ${open ? "is-open" : ""}`}
        aria-hidden={!open}
      >
        <div className="nav-mobile-inner container">
          {SECTION_LINKS.map((link, i) => (
            <button
              key={link.id}
              type="button"
              className="nav-mobile-link"
              style={{ transitionDelay: open ? `${0.06 + i * 0.05}s` : "0s" }}
              tabIndex={open ? 0 : -1}
              onClick={() => goToSection(link.id)}
            >
              {link.label}
            </button>
          ))}
          <Link
            to="/apps"
            className="nav-mobile-link nav-mobile-link--apps"
            style={{ transitionDelay: open ? "0.16s" : "0s" }}
            tabIndex={open ? 0 : -1}
          >
            Apps
            <span className="nav-mobile-apps-hint">ShopList · Stack Clash</span>
          </Link>
          <div
            className="nav-mobile-social"
            style={{ transitionDelay: open ? "0.21s" : "0s" }}
          >
            <a
              href={SITE.github}
              target="_blank"
              rel="noopener noreferrer"
              tabIndex={open ? 0 : -1}
            >
              GitHub
            </a>
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              tabIndex={open ? 0 : -1}
            >
              LinkedIn
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
